import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { countries } from '@repo/toolkit/constants/countries'
import Badge from '@repo/ui/badge'
import ScrollableDiv from '@repo/ui/scrollable-div'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'

import { getStaffIdDocumentRules } from '../../../../api/staff/get-staff-id-document-rules'
import { getStaffApplication } from '../../../../api/staff-application/get-staff-application'
import useTranslate from '../../../../lib/hooks/use-translate'
import {
  DocumentStatusEnum,
  idStatusEnumMapping
} from '../../../../lib/types/enums/common/document-status.enum'
import { IDTypeEnum, IDTypeEnumMapping } from '../../../../lib/types/enums/common/id-type.enum'
import { isStaffEnglish } from '../../../../lib/types/enums/staff/staff-qualifications.enum'
import { AdminStaffInput } from '../../../../lib/types/models/staff/admin-staff-input'
import { getSelectedOption } from '../../../../lib/utils/functions/misc.functions'
import ControlledSwitch from '../../../elements/controlled-switch'
import { FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledDateStringInput from '../../../elements/inputs/controlled-date-string-input'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledPhoneInput from '../../../elements/inputs/controlled-phone-input'
import ControlledRadioInput from '../../../elements/inputs/controlled-radio-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import DocumentCard from '../document-card'
import { isStaffPaySlipRequired } from '../utils/diplomas-required-documents'

type Props = {
  staffId?: number
}

const StaffAdministrativeDocumentsForm = ({ staffId }: Props) => {
  const form = useFormContext<AdminStaffInput>()
  const { t } = useTranslate()

  const etaFile = form.watch('eta')
  const idFile = form.watch('idFile')
  const idStatus = form.watch('idStatus')
  const diplomas = form.watch('diplomas')
  const position = form.watch('position')
  const paySlipFile = form.watch('paySlip')
  const applicationId = form.watch('applicationId')
  const criminalRecordFile = form.watch('criminalRecord')
  const isEnglishStaff = isStaffEnglish({ qualifications: form.watch('qualifications') })

  const { data: staffApplication } = useQuery({
    queryKey: ['staff-application', applicationId],
    queryFn: () => {
      if (applicationId) return getStaffApplication(applicationId)
    },
    enabled: !!applicationId
  })

  const { data: idDocumentRules } = useQuery({
    queryKey: ['staff-id-document-rules', staffId],
    queryFn: () => {
      if (staffId) return getStaffIdDocumentRules(staffId)
    },
    enabled: !!staffId
  })

  // The identity card cannot be chosen while a stay of the staff requires a passport
  const isIdTypeDisabled = (idType: IDTypeEnum) =>
    !!idDocumentRules?.passportRequired && idType === IDTypeEnum.IDENTITY_CARD

  // An empty identity document starts on the identity card, move it to the passport when required
  const idType = form.watch('idType')
  const idNumber = form.watch('idNumber')
  useEffect(() => {
    if (
      idDocumentRules?.passportRequired &&
      idType === IDTypeEnum.IDENTITY_CARD &&
      !idNumber &&
      !idFile
    )
      form.setValue('idType', IDTypeEnum.PASSPORT, { shouldDirty: true })
  }, [form, idDocumentRules?.passportRequired, idType, idNumber, idFile])

  const hasPaySlip = isStaffPaySlipRequired({
    position,
    qualifications: form.watch('qualifications'),
    diplomas,
    memberOfNationalEducationStatus: staffApplication?.memberOfNationalEducationStatus
  })

  return (
    <div>
      <ScrollableDiv className="flex w-full gap-5">
        {idStatus && (
          <DocumentCard
            title="Pièce d'identité"
            className={cn({ 'w-72 flex-shrink-0': hasPaySlip })}
            mapping={idStatusEnumMapping}
            status={idStatus}
            customBadge={
              <Badge
                variant={idStatusEnumMapping[idStatus].variant}
                icon={idStatusEnumMapping[idStatus].icon}
                text={t(idStatusEnumMapping[idStatus].label)}
                iconPosition="start"
              />
            }
            statusPosition="outside"
            files={idFile ? [idFile] : []}
            onFilesChange={(files) => {
              form.setValue('idFile', files[0])
              if (files[0]) {
                form.setValue('idStatus', DocumentStatusEnum.RECEIVED)
              } else form.setValue('idStatus', DocumentStatusEnum.PENDING)
            }}
          >
            <div className="flex flex-col gap-3">
              <Typography variant={'tag'} color={tailwindColors.grey.DEFAULT}>
                Type:
              </Typography>
              <div className="flex items-center gap-4 mt-2">
                {Object.values(IDTypeEnum).map((id) => (
                  <FormField
                    key={id}
                    control={form.control}
                    name="idType"
                    render={({ field }) => {
                      return (
                        <FormItem>
                          <FormControl>
                            <ControlledRadioInput
                              {...field}
                              disabled={field.disabled || isIdTypeDisabled(id)}
                              label={t(IDTypeEnumMapping[id].title)}
                              checked={field.value === id}
                              onCheck={() => field.onChange(id)}
                            />
                          </FormControl>
                        </FormItem>
                      )
                    }}
                  />
                ))}
              </div>
              <div className="h-2" />
              <FormField
                control={form.control}
                name="idNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledInput {...field} label="N°" type="text" />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="idDeliveredAt"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledDateStringInput {...field} label="Date de délivrance" />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="idValidUntil"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledDateStringInput {...field} label="Date d'expiration" />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="idDeliveredBy"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledSelectInput
                        {...field}
                        options={countries.map((country) => ({ label: country, value: country }))}
                        label={'Pays émetteur'}
                        searchable
                        selectedOption={getSelectedOption(field.value, field.value)}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </DocumentCard>
        )}

        {/* check if he has the required diploma or if he is a member of national education */}
        {hasPaySlip && (
          <DocumentCard
            requireVerification
            className={cn({ 'w-72 flex-shrink-0': hasPaySlip })}
            disabled={form.formState.defaultValues?.paySlipStatus === DocumentStatusEnum.VERIFIED}
            title="Bulletin de salaire"
            errorMessage={form.formState.errors.paySlip?.message}
            statusPosition="outside"
            status={form.watch('paySlipStatus')}
            onStatusChange={(status) => {
              form.setValue('paySlipStatus', status)
            }}
            files={paySlipFile ? [paySlipFile] : []}
            onFilesChange={(files) => {
              form.setValue('paySlip', files[0], { shouldValidate: true })
              if (files[0]) form.setValue('paySlipStatus', DocumentStatusEnum.RECEIVED)
              else form.setValue('paySlipStatus', DocumentStatusEnum.PENDING)
            }}
          />
        )}
        <DocumentCard
          requireVerification
          disabled={
            form.formState.defaultValues?.criminalRecordStatus === DocumentStatusEnum.VERIFIED
          }
          className={cn({ 'w-72 flex-shrink-0': hasPaySlip })}
          title="Casier judiciaire"
          statusPosition="outside"
          status={form.watch('criminalRecordStatus')}
          onStatusChange={(status) => {
            form.setValue('criminalRecordStatus', status)
          }}
          files={criminalRecordFile ? [criminalRecordFile] : []}
          onFilesChange={(files) => {
            form.setValue('criminalRecord', files[0])
            if (files[0]) form.setValue('criminalRecordStatus', DocumentStatusEnum.RECEIVED)
            else form.setValue('criminalRecordStatus', DocumentStatusEnum.PENDING)
          }}
        />
        {!isEnglishStaff && (
          <DocumentCard
            requireVerification
            disabled={form.formState.defaultValues?.etaStatus === DocumentStatusEnum.VERIFIED}
            title="ETA"
            className={cn({ 'w-72 flex-shrink-0': hasPaySlip })}
            statusPosition="outside"
            status={form.watch('etaStatus')}
            onStatusChange={(status) => {
              form.setValue('etaStatus', status)
            }}
            files={etaFile ? [etaFile] : []}
            onFilesChange={(files) => {
              form.setValue('eta', files[0])
              if (files[0]) form.setValue('etaStatus', DocumentStatusEnum.RECEIVED)
              else form.setValue('etaStatus', DocumentStatusEnum.PENDING)
            }}
          />
        )}
      </ScrollableDiv>
      <div className="h-5" />
      <Typography variant={'tag'} color={tailwindColors.grey.DEFAULT}>
        Coordonnées bancaires
      </Typography>
      <div className="h-4" />
      <div className="flex w-full gap-5">
        <FormField
          control={form.control}
          name="iban"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput {...field} label="IBAN" type="text" />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="bic"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput {...field} label="BIC" type="text" />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="h-4" />
      <div className="flex w-full gap-5">
        <FormField
          control={form.control}
          name="hasComplemenaryInsurance"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledSwitch
                  {...field}
                  label="A-t-il une assurance complémentaire?"
                  withBorder
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="complementaryInsuranceName"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput
                  {...field}
                  label="NOM de l'assurance complémentaire"
                  type="text"
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      <div className="h-4" />
      <div className="flex w-full gap-5">
        <FormField
          control={form.control}
          name="emergencyContactName"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput {...field} label="NOM du contact d'urgence" type="text" />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="emergencyContactRelationship"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput {...field} label="Relation du contact d'urgence" type="text" />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="h-4" />
      <FormField
        control={form.control}
        name="emergencyContactPhoneNumber"
        render={({ field }) => (
          <FormItem className="w-full">
            <FormControl>
              <ControlledPhoneInput
                {...field}
                label="Téléphone du contact d'urgence"
                defaultCountry="FR"
              />
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  )
}

export default StaffAdministrativeDocumentsForm
